<template>
  <article class="price-list">
    <slot v-if="$slots.default" />

    <LcSeparator class="my-200" />

    <LcDescriptionList class="gap-050">
      <dt class="subtotal">
        {{ $t('pricelist.labels.subtotal') }}
      </dt>
      <dd class="subtotal text-right">{{ chargeTotal }}</dd>

      <template v-if="showConvenienceFee">
        <dt class="flex gap-100 items-center">
          {{ convenienceFeeLabel }}

          <LcInfoButton
            :key="`infobutton_${locale}`"
            :headline="$t('infobutton.conveniencefee.headline')"
            :open-button-aria-label="$t('infobutton.conveniencefee.label')"
            tabindex="-1"
            :text="$t('infobutton.conveniencefee.message')"
          />
        </dt>
        <dd class="text-right">{{ convenienceFee }}</dd>
      </template>

      <template v-if="showSalesTax">
        <dt>{{ $t('pricelist.labels.salestax') }}</dt>
        <dd class="text-right">{{ salesTax }}</dd>
      </template>
    </LcDescriptionList>
  </article>
</template>

<script lang="ts" setup>
import type { LicensedTerritory } from '@lottocom/lottery-mapping'
import type Money from '~~/models/Money'

type PriceListProps = {
  chargeTotal: Money
  convenienceFee: Money
  salesTax: Money
  showSalesTax?: boolean
}

const { convenienceFee, showSalesTax = false } = defineProps<PriceListProps>()

const { locale, t } = useI18n()
const licensedTerritory = useLicensedTerritory({ raw: true })

const showConvenienceFee = computed(() => convenienceFee.value > 0)
const convenienceFeeLabel = computed(() => {
  const isPriceTransparencyEnabled = useIsPriceTransparencyEnabled(
    licensedTerritory as LicensedTerritory,
  )
  return isPriceTransparencyEnabled
    ? t('common.words.conveniencefee')
    : t('pricelist.labels.conveniencefee')
})
</script>

<style lang="scss" scoped>
.price-list {
  padding-right: 1.5rem;

  .subtotal {
    @include token-headline-h5-headline;
  }
}
</style>
